import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { GATES, JURISDICTIONS } from '../mock';
import ClosingCTA from '../components/ClosingCTA';

export default function NeuralWorksSecurity() {
  return (
    <div>
      <section className="pt-16 pb-10">
        <div className="dwx">
          <Link to="/neuralworks" className="inline-flex items-center gap-2 mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60 hover:text-[#1A1A1A]">
            <ArrowLeft size={14} /> Back to NeuralWorks
          </Link>
          <div className="mt-8 mono text-[11px] tracking-[0.22em] uppercase text-[#5B4FCF]">Layer 3 · Governance & Control</div>
          <h1 className="serif-tight text-[54px] md:text-[92px] leading-[0.94] mt-4">Governance for agents that act.</h1>
          <p className="mt-8 max-w-3xl text-[17px] leading-relaxed text-[#1A1A1A]/80">Most AI governance frameworks address what a model may say. Ours addresses what an agent may do — to your data, your systems and your customers.</p>
        </div>
      </section>

      {/* Gates */}
      <section className="py-16">
        <div className="dwx">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">Risk gates</div>
          <h2 className="serif-tight text-[38px] md:text-[52px] leading-[0.98] mt-3 max-w-3xl">Not every action deserves the same scrutiny.</h2>
          <p className="mt-6 max-w-3xl text-[15px] leading-relaxed text-[#1A1A1A]/75">Reading a report is not deleting a table. NeuralWorks classifies every agent action on four axes — action type, scope, reversibility, novelty — and routes it to one of four gates.</p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4">
            {GATES.map((g) => {
              const map = { green: '#22A94F', caution: '#B5822A', indigo: '#5B4FCF', block: '#A63D2F' };
              return (
                <div key={g.tier} className="rounded-[16px] bg-[#F4F2EC] p-6 border border-[#1A1A1A]/10">
                  <div className="h-1 w-10 rounded-full mb-4" style={{ background: map[g.color] }} />
                  <div className="mono text-[11px] tracking-[0.22em] uppercase" style={{ color: map[g.color] }}>Tier {g.tier}</div>
                  <div className="serif text-[22px] mt-2">{g.name}</div>
                  <div className="mt-2 text-[13px] leading-relaxed text-[#1A1A1A]/75">{g.behaviour}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 rounded-[16px] bg-[#FBFAF6] border border-[#5B4FCF]/30 p-6 text-[14px] leading-relaxed text-[#1A1A1A]/85 max-w-3xl">
            <span className="mono text-[10px] tracking-[0.22em] uppercase text-[#5B4FCF]">Hard scope guards</span>
            <p className="mt-2">Above all four sit hard scope guards — maximum records per action, a tool allowlist per initiative, rate ceilings on even the lowest tier. Guards run <em>before</em> classification. If an action trips one, it is blocked regardless of how harmless its tier would have been.</p>
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section className="py-16 bg-[#F4F2EC]">
        <div className="dwx grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">Privacy</div>
            <h2 className="serif-tight text-[38px] md:text-[52px] leading-[0.98] mt-3">The action stays on the record.<br /><span className="text-[#1A1A1A]/50">The identity goes.</span></h2>
          </div>
          <div className="col-span-12 md:col-span-7 text-[15px] leading-relaxed text-[#1A1A1A]/80 space-y-4">
            <p>Personal data lives in a separate erasable store, referenced by internal ID. Where personal data must appear in a log, it is written as a token.</p>
            <p>An erasure request deletes both mappings in a single operation across every store. The audit trail survives intact; the person is no longer in it.</p>
          </div>
        </div>
      </section>

      {/* Data residency */}
      <section className="py-16">
        <div className="dwx">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">Data residency</div>
          <h2 className="serif-tight text-[38px] md:text-[52px] leading-[0.98] mt-3 max-w-3xl">Cloud-agnostic, by design and by necessity.</h2>
          <p className="mt-6 max-w-3xl text-[15px] leading-relaxed text-[#1A1A1A]/75">Containment, identity and audit are architectural properties of NeuralWorks, not features of a particular cloud. The same framework deploys wherever your obligations require — a specific region, your own infrastructure, or fully in-country — without re-engineering the control model.</p>

          <div className="mt-10 rounded-[20px] border border-[#1A1A1A]/10 overflow-hidden">
            <table className="w-full text-[14px]">
              <thead className="bg-[#F4F2EC] text-left text-[#1A1A1A]/70">
                <tr>
                  <th className="p-4 mono text-[11px] tracking-[0.22em] uppercase">Region</th>
                  <th className="p-4 mono text-[11px] tracking-[0.22em] uppercase">Regime</th>
                  <th className="p-4 mono text-[11px] tracking-[0.22em] uppercase">Deployment</th>
                </tr>
              </thead>
              <tbody className="bg-[#FBFAF6]">
                {JURISDICTIONS.map((j) => (
                  <tr key={j.region} className="border-t border-[#1A1A1A]/8">
                    <td className="p-4 font-medium">{j.region}</td>
                    <td className="p-4 text-[#1A1A1A]/75">{j.regime}</td>
                    <td className="p-4 text-[#1A1A1A]/75">{j.deployment}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <ClosingCTA />
    </div>
  );
}
